import React, { createContext, useContext } from 'react';
import { create } from 'zustand';
import { employeesApi } from '../../../../services/api';

// Create and export the context
export const EmployeeSearchContext = createContext(null);

// Factory function to create independent store instances
const createEmployeeSearchStore = () => {
  const initialState = {
    employees: [],
    searchTerm: '',
    orderBy: 'fullName',
    orderDirection: 'asc',
    limit: 20,
    hasNextPage: false,
    endCursor: null,
    totalCount: 0,
    loading: false,
    error: null,
  };

  return create((set, get) => {
    const buildParams = (overrides = {}) => {
      const state = get();
      return {
        limit: state.limit,
        orderBy: state.orderBy,
        orderDirection: state.orderDirection,
        search: overrides.search !== undefined ? overrides.search : state.searchTerm,
        cursor: overrides.cursor !== undefined ? overrides.cursor : state.endCursor,
        ...overrides,
      };
    };

    const fetchEmployees = async ({
      params,
      append = false,
      operation = 'fetch',
      stateUpdates = {},
    }) => {
      try {
        set({ loading: true, error: null, ...stateUpdates });

        const response = await employeesApi.getAllEmployeesLite(params);

        set({
          employees: append
            ? [...get().employees, ...(response.items || [])]
            : response.items || [],
          hasNextPage: response.pageInfo?.hasNextPage || false,
          endCursor: response.pageInfo?.endCursor || null,
          totalCount: response.pageInfo?.totalCount || 0,
        });
      } catch (error) {
        console.error(`Error ${operation} employees`);
        set({ error: error.message || `Failed to ${operation} employees` });
      } finally {
        set({ loading: false });
      }
    };

    return {
      ...initialState,
      initialize: async (searchParams = {}) => {
        if (get().loading) return;
        await fetchEmployees({
          params: buildParams(searchParams),
          operation: 'initialize',
          stateUpdates: { searchTerm: searchParams.search || '' },
        });
      },
      loadMore: async () => {
        if (get().loading || !get().hasNextPage) return;
        await fetchEmployees({
          params: buildParams({ cursor: get().endCursor }),
          append: true,
          operation: 'load more',
        });
      },
      searchEmployees: async (searchTerm) => {
        if (get().loading || get().searchTerm === searchTerm) return;
        await fetchEmployees({
          params: buildParams({ search: searchTerm, cursor: null }),
          operation: 'search',
          stateUpdates: { searchTerm },
        });
      },
      reset: () => set({ ...initialState }),
    };
  });
};

// Provider component
export const EmployeeSearchProvider = ({ children, instanceId }) => {
  const storeRef = React.useRef(null);
  
  if (!storeRef.current) {
    storeRef.current = createEmployeeSearchStore();
  }

  return (
    <EmployeeSearchContext.Provider value={storeRef.current}>
      {children}
    </EmployeeSearchContext.Provider>
  );
};

// Custom hook to use the store
export const useEmployeeSearchStore = () => {
  const store = useContext(EmployeeSearchContext);
  if (!store) {
    throw new Error('useEmployeeSearchStore must be used within an EmployeeSearchProvider');
  }
  return store;
};